import { useMemo } from 'react';

import type { MeObject } from '@crehana/auth';
import { type ILearningMenuLink, useB2bRoutes } from '@crehana/compass.layouts';
import type { CrehanaTFunction } from '@crehana/i18n';
import type { NestedRouteType, RouteType, SingleRouteType } from '@crehana/web';

import type { LearnDashboardLocaleKeys } from '@/views/LearnDashboard/types';

import useRoutesExtraData from './useRoutesExtraData';

interface UseRouteArgs {
  me?: MeObject | null;
  organizationId: number;
  organizationSlug?: string;
  isCrossNavigationSidebarActive?: boolean;
}

const translateI18nRoutes = (
  t: CrehanaTFunction<LearnDashboardLocaleKeys>,
  routes: RouteType[] | SingleRouteType[],
) => {
  return routes.map(route => {
    if ((route as NestedRouteType).nested?.length) {
      (route as NestedRouteType).nested = translateI18nRoutes(
        t,
        (route as NestedRouteType).nested!,
      ) as SingleRouteType[];
    }

    if (route.label) {
      route.label = t(route.label as LearnDashboardLocaleKeys);
    }

    if ((route as SingleRouteType).contentTitle) {
      (route as SingleRouteType).contentTitle = t(
        (route as SingleRouteType).contentTitle as LearnDashboardLocaleKeys,
      );
    }

    if ((route as SingleRouteType).decorations?.subTitle) {
      (route as SingleRouteType).decorations!.subTitle = t(
        (route as SingleRouteType).decorations!
          .subTitle as LearnDashboardLocaleKeys,
      );
    }

    return route;
  });
};

interface UseRoutesReturnType {
  routes: RouteType[];
  defaultRoute?: SingleRouteType;
  isLoading: boolean;
  isKnowledgeHubEnabled: boolean;
}

const useLearnDashboardRoutes = ({
  me,
  organizationId,
  organizationSlug,
  isCrossNavigationSidebarActive = false,
}: UseRouteArgs): UseRoutesReturnType => {
  const { routesExtraData } = useRoutesExtraData({
    isCrossNavigationSidebarActive,
  });

  const { b2bRoutes, isLoading, isKnowledgeHubEnabled } = useB2bRoutes({
    organizationSlug: organizationSlug || '',
    productPlatform: 'learning',
    email: me?.email || '',
    routesExtraData,
    organizationId,
  });

  const routes = useMemo(() => mapRoutesToRoutesType(b2bRoutes), [b2bRoutes]);
  const defaultRoute = routes[0] as SingleRouteType | undefined;

  return {
    routes,
    defaultRoute,
    isLoading,
    isKnowledgeHubEnabled,
  };
};

export default useLearnDashboardRoutes;

const mapRoutesToRoutesType = (
  learningLinks: ILearningMenuLink[],
): RouteType[] => {
  return learningLinks.map(link => {
    if (link.nested) {
      return {
        ...link,
        key: link.code,
        nested: link.nested.map(
          nestedLink =>
            ({
              ...nestedLink,
              path: nestedLink.url,
              key: nestedLink.code,
              exact: nestedLink.exact,
            }) as SingleRouteType,
        ),
      } as NestedRouteType;
    }
    return {
      ...link,
      key: link.code,
      path: link.url || '',
      exact: !!link.exact,
    } as SingleRouteType;
  });
};
